import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  CalendarDays, Clock, MapPin, MessageCircle, ChevronRight, 
  LogOut, Loader2, CheckCircle2, AlertCircle, Sparkles, User
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isPast, isFuture } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const statusConfig = {
    draft: { color: 'bg-slate-500', label: 'Scheduled' },
    confirmed: { color: 'bg-blue-500', label: 'Confirmed' },
    assigned: { color: 'bg-purple-500', label: 'Assigned' },
    accepted: { color: 'bg-indigo-500', label: 'Ready' },
    in_progress: { color: 'bg-amber-500', label: 'In Progress' },
    completed: { color: 'bg-green-500', label: 'Completed' },
    cancelled: { color: 'bg-red-500', label: 'Cancelled' },
  };

  const config = statusConfig[status] || { color: 'bg-slate-500', label: status };

  return (
    <span className={`${config.color} text-white text-xs font-medium px-2.5 py-1 rounded-full`}>
      {config.label}
    </span>
  );
}

function BookingCard({ booking, onClick }) {
  const scheduledDate = parseISO(booking.scheduled_date);
  const isUpcoming = isFuture(scheduledDate);
  const isActive = booking.status === 'in_progress';

  let dateLabel = format(scheduledDate, 'EEE, MMM d');
  if (isToday(scheduledDate)) dateLabel = 'Today';
  else if (isTomorrow(scheduledDate)) dateLabel = 'Tomorrow';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] ${
        isActive 
          ? 'bg-amber-500/10 border-amber-500/50 hover:border-amber-400' 
          : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
      }`}
      data-testid={`booking-card-${booking.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Date & Time */}
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
            <CalendarDays className="w-4 h-4" />
            <span>{dateLabel}</span>
            {booking.time_window_start && (
              <>
                <Clock className="w-4 h-4 ml-2" />
                <span>{booking.time_window_start}</span>
              </>
            )}
          </div>

          {/* Address */}
          <div className="flex items-start gap-2 mb-3">
            <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-white font-medium truncate">{booking.service_address}</p>
          </div>

          {/* Status & Unread */}
          <div className="flex items-center gap-3">
            <StatusBadge status={booking.status} />
            {booking.unread_comments > 0 && (
              <span className="flex items-center gap-1 text-amber-400 text-xs">
                <MessageCircle className="w-3.5 h-3.5" />
                {booking.unread_comments} new
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-slate-500" />
      </div>
    </button>
  );
}

export default function ClientPortalDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [user, setUser] = useState(null);

  const getToken = () => localStorage.getItem('portal_token');

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      // Fetch dashboard summary
      const dashRes = await fetch(`${API_URL}/api/client-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (dashRes.status === 401 || dashRes.status === 403) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        navigate('/portal/login');
        return;
      }

      if (!dashRes.ok) throw new Error('Failed to load dashboard');
      const dashData = await dashRes.json();
      setDashboard(dashData);

      // Fetch bookings
      const bookRes = await fetch(`${API_URL}/api/client-portal/my-bookings?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!bookRes.ok) throw new Error('Failed to load bookings');
      const bookData = await bookRes.json();
      setBookings(bookData.bookings || []);

      // Load user
      const savedUser = localStorage.getItem('portal_user');
      if (savedUser) setUser(JSON.parse(savedUser));

    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    toast.success('Logged out');
    navigate('/portal/login');
  };

  // Filter bookings by tab
  const filteredBookings = bookings.filter(b => {
    if (activeTab === 'upcoming') {
      return ['draft', 'confirmed', 'assigned', 'accepted', 'in_progress'].includes(b.status);
    } else {
      return b.status === 'completed';
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">My Bookings</h1>
                <p className="text-sm text-slate-400">{dashboard?.client_name || 'Client Portal'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">{dashboard.stats.upcoming}</div>
              <div className="text-xs text-slate-400 mt-1">Upcoming</div>
            </div>
            <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{dashboard.stats.in_progress}</div>
              <div className="text-xs text-slate-400 mt-1">In Progress</div>
            </div>
            <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{dashboard.stats.completed}</div>
              <div className="text-xs text-slate-400 mt-1">Completed</div>
            </div>
          </div>
        )}

        {/* Unread Messages Alert */}
        {dashboard?.stats?.unread_messages > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-amber-400" />
            <span className="text-amber-200">
              You have <strong>{dashboard.stats.unread_messages}</strong> unread message{dashboard.stats.unread_messages > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'upcoming'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white'
            }`}
            data-testid="tab-upcoming"
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'completed'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white'
            }`}
            data-testid="tab-completed"
          >
            Completed
          </button>
        </div>

        {/* Bookings List */}
        <div className="space-y-3">
          {filteredBookings.length === 0 ? (
            <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-8 text-center">
              {activeTab === 'upcoming' ? (
                <>
                  <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No upcoming bookings</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Contact us to schedule your next clean
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No completed bookings yet</p>
                </>
              )}
            </div>
          ) : (
            filteredBookings.map(booking => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onClick={() => navigate(`/portal/booking/${booking.id}`)}
              />
            ))
          )}
        </div>

        {/* Contact Footer */}
        <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-6 text-center mt-8">
          <h3 className="text-white font-medium mb-2">Need to make changes?</h3>
          <p className="text-slate-400 text-sm mb-4">
            Contact us to reschedule or request additional services
          </p>
          <a
            href="tel:+61733333333"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
          >
            Contact Us
          </a>
        </div>
      </main>
    </div>
  );
}
